import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  MapPin,
  Search,
  ArrowRight,
  Info,
  Building2,
  Save,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

function useDebouncedValue(value, delay = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export default function NewCustomerPage() {
  const navigate = useNavigate();

  const [placesConfigured, setPlacesConfigured] = useState(true);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, 350);
  const [searching, setSearching] = useState(false);
  const [predictions, setPredictions] = useState([]);
  const [selected, setSelected] = useState(null); // Place details
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Manual form fields (populated after selection, editable)
  const [businessName, setBusinessName] = useState("");
  const [address, setAddress] = useState("");
  const [placeId, setPlaceId] = useState("");
  const [googleMapsUrl, setGoogleMapsUrl] = useState("");
  const [googleReviewUrl, setGoogleReviewUrl] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const lastSlugRef = useRef("");

  useEffect(() => {
    api
      .get("/places/status")
      .then((r) => setPlacesConfigured(!!r.data?.configured))
      .catch(() => setPlacesConfigured(false));
  }, []);

  useEffect(() => {
    let active = true;
    const run = async () => {
      if (!placesConfigured) return;
      if (!debouncedQuery || debouncedQuery.trim().length < 2) {
        setPredictions([]);
        return;
      }
      setSearching(true);
      try {
        const { data } = await api.get("/places/autocomplete", {
          params: { q: debouncedQuery.trim() },
        });
        if (active) setPredictions(data?.results || []);
      } catch (e) {
        if (active) toast.error(formatApiError(e));
      } finally {
        if (active) setSearching(false);
      }
    };
    run();
    return () => {
      active = false;
    };
  }, [debouncedQuery, placesConfigured]);

  const canSave = useMemo(
    () => businessName.trim() && address.trim() && googleReviewUrl.trim() && slug.trim(),
    [businessName, address, googleReviewUrl, slug]
  );

  // Auto-suggest slug from business name (until user edits it manually).
  const autoSlugFromName = useCallback(
    async (name) => {
      const proposal = name
        .toLowerCase()
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/ß/g, "ss")
        .replace(/ä/g, "ae")
        .replace(/ö/g, "oe")
        .replace(/ü/g, "ue")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 80);
      if (!proposal) return "";
      try {
        const { data } = await api.get("/customers/slug-check", { params: { slug: proposal } });
        return data.available_slug;
      } catch {
        return proposal;
      }
    },
    []
  );

  const selectPrediction = async (p) => {
    if (!p?.place_id) return;
    setSelected(null);
    setLoadingDetails(true);
    try {
      const { data } = await api.get(`/places/details/${p.place_id}`);
      setSelected(data);
      setBusinessName(data.business_name || "");
      setAddress(data.address || "");
      setPlaceId(data.place_id || "");
      setGoogleMapsUrl(data.google_maps_url || "");
      setGoogleReviewUrl(data.google_review_url || "");
      setPredictions([]);
      setQuery(data.business_name || "");
      if (!slugTouched) {
        const s = await autoSlugFromName(data.business_name || "");
        setSlug(s);
        lastSlugRef.current = s;
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoadingDetails(false);
    }
  };

  const onSlugBlur = async () => {
    if (!slug.trim()) return;
    try {
      const { data } = await api.get("/customers/slug-check", { params: { slug } });
      if (!data.is_available) {
        setSlug(data.available_slug);
        toast.message(`Slug angepasst zu "${data.available_slug}" (bereits belegt).`);
      }
    } catch {
      /* ignore */
    }
  };

  const onSave = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    setSaving(true);
    try {
      const { data } = await api.post("/customers", {
        business_name: businessName.trim(),
        address: address.trim(),
        place_id: placeId || null,
        google_maps_url: googleMapsUrl || null,
        google_review_url: googleReviewUrl.trim(),
        slug: slug.trim(),
        status: "active",
      });
      toast.success("Kunde gespeichert.");
      navigate(`/kunden/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="new-customer-page">
      <div>
        <Link
          to="/kunden"
          className="text-xs text-muted-foreground hover:text-foreground"
          data-testid="back-to-customers"
        >
          ← Zurück zu Kunden
        </Link>
        <h1 className="font-heading text-3xl font-bold tracking-tight mt-2">
          Neue Firma hinzufügen
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Suche das Google-Profil, prüfe die Daten und lege den Bewertungslink an.
        </p>
      </div>

      {!placesConfigured && (
        <div
          data-testid="places-not-configured-banner"
          className="card-surface p-4 flex items-start gap-3 border-amber-500/20 bg-amber-500/[0.06]"
        >
          <AlertCircle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-medium text-amber-200">
              Google Maps API-Key noch nicht eingerichtet.
            </div>
            <p className="text-amber-200/70 mt-1">
              Du kannst Firmen weiterhin manuell erfassen. Für die automatische Suche
              bitte den <span className="text-amber-100">GOOGLE_MAPS_API_KEY</span>{" "}
              in den Einstellungen hinterlegen.
            </p>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="card-surface p-5">
        <Label className="label-eyebrow">Google-Suche</Label>
        <div className="relative mt-2">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="places-search-input"
            placeholder="z. B. „Am Kleinen Bär Hannover“"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            disabled={!placesConfigured}
            className="pl-9 h-11 bg-background/50"
          />
          {searching && (
            <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground animate-spin" />
          )}
        </div>

        {predictions.length > 0 && (
          <ul
            data-testid="places-predictions-list"
            className="mt-3 divide-y divide-white/[0.05] border border-white/[0.06] rounded-lg overflow-hidden"
          >
            {predictions.map((p) => (
              <li key={p.place_id}>
                <button
                  type="button"
                  data-testid={`prediction-${p.place_id}`}
                  onClick={() => selectPrediction(p)}
                  className="w-full text-left px-4 py-3 hover:bg-white/[0.04] flex items-start gap-3"
                >
                  <MapPin className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{p.main_text}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {p.secondary_text}
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-muted-foreground ml-auto" />
                </button>
              </li>
            ))}
          </ul>
        )}

        {loadingDetails && (
          <div className="mt-3 text-xs text-muted-foreground flex items-center gap-2">
            <Loader2 className="w-3 h-3 animate-spin" /> Details werden geladen…
          </div>
        )}
      </div>

      {/* Review / edit form */}
      <form onSubmit={onSave} className="card-surface p-5 space-y-5" data-testid="customer-form">
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-primary" />
          <h2 className="font-heading text-lg font-semibold">Firmendaten prüfen</h2>
        </div>

        <div className="grid gap-4">
          <Field
            label="Firmenname"
            value={businessName}
            onChange={setBusinessName}
            testId="input-business-name"
            required
          />
          <Field
            label="Adresse"
            value={address}
            onChange={setAddress}
            testId="input-address"
            required
          />
          <div className="grid sm:grid-cols-2 gap-4">
            <Field
              label="Place ID"
              value={placeId}
              onChange={setPlaceId}
              testId="input-place-id"
              readOnly
              placeholder="—"
            />
            <Field
              label="Google Maps URL"
              value={googleMapsUrl}
              onChange={setGoogleMapsUrl}
              testId="input-google-maps-url"
              placeholder="https://maps.google.com/..."
            />
          </div>
          <Field
            label="Google-Bewertungslink"
            value={googleReviewUrl}
            onChange={setGoogleReviewUrl}
            testId="input-review-url"
            required
            hint="Wird automatisch ausgefüllt (writeAReviewUri). Manuell überschreibbar."
          />
          <div>
            <Label className="label-eyebrow">Custom Slug</Label>
            <div className="mt-2 flex items-stretch rounded-lg border border-white/[0.08] bg-background/50 overflow-hidden">
              <span className="px-3 py-2 text-xs text-muted-foreground border-r border-white/[0.06] bg-white/[0.02] whitespace-nowrap self-center">
                /r/
              </span>
              <input
                data-testid="input-slug"
                value={slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setSlug(e.target.value.toLowerCase().replace(/\s+/g, "-"));
                }}
                onBlur={onSlugBlur}
                required
                className="flex-1 bg-transparent px-3 py-2 text-sm outline-none focus:ring-0"
                placeholder="am-kleinen-baer-hannover"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1">
              <Info className="w-3 h-3" /> Muss eindeutig sein. Doppelte Slugs werden
              automatisch nummeriert.
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <Button
            type="submit"
            className="h-11 rounded-full px-6 gap-2"
            disabled={!canSave || saving}
            data-testid="save-customer-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Kunde speichern
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate("/kunden")}
            className="h-11 rounded-full"
            data-testid="cancel-customer-btn"
          >
            Abbrechen
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, testId, required, readOnly, hint, placeholder }) {
  return (
    <div>
      <Label className="label-eyebrow">
        {label} {required ? <span className="text-primary">*</span> : null}
      </Label>
      <Input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        readOnly={readOnly}
        placeholder={placeholder}
        data-testid={testId}
        className="mt-2 bg-background/50 h-11"
      />
      {hint ? <p className="text-xs text-muted-foreground mt-1.5">{hint}</p> : null}
    </div>
  );
}
